import { useEffect, useState } from "react";
import { CallBar, CallView, IncomingCall } from "../calls/CallView.tsx";
import { useCall } from "../calls/controller.ts";
import { useServices } from "./services.ts";
import { ChatList } from "../chats/ChatList.tsx";
import { Conversation } from "../chats/Conversation.tsx";
import { TopicPanel } from "../chats/TopicPanel.tsx";
import { BoardList } from "../board/BoardList.tsx";
import { BoardView } from "../board/BoardView.tsx";
import { DocumentList } from "../documents/DocumentList.tsx";
import { DocumentView } from "../documents/DocumentView.tsx";
import { useStore } from "../data/store.ts";
import { Settings } from "../settings/Settings.tsx";
import { Rail, type Section } from "../shell/Rail.tsx";
import { navigate, useRoute } from "./router.ts";
import styles from "./Shell.module.css";

/** Rail, chat list, conversation and the panel on the right (docs/web-designs screens 2 and 4). */
export function Shell() {
  const route = useRoute();
  const { calls } = useServices();
  const call = useCall(calls);
  const [minimized, setMinimized] = useState(false);
  // A new call comes to the front.
  useEffect(() => setMinimized(false), [call?.callId, call?.phase === "ended"]);
  const inCall = Boolean(call && call.phase !== "incoming");
  const { unread, agent, total } = useStore((s) => {
    const all = [...s.conversations.values()];
    return { unread: all.reduce((n, c) => n + (c.muted_until && Date.parse(c.muted_until) > Date.now() ? 0 : c.unread_count), 0), agent: all.find((c) => c.kind === "agent")?.id, total: all.length };
  });

  // "Voxie" in the rail is the chat with Voxie.
  useEffect(() => {
    if (route.screen === "voxie" && agent) navigate({ screen: "chats", conversation: agent }, true);
  }, [route.screen, agent]);

  // Title and favicon show what is unread (docs/web-konzept.md, section 8).
  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) Voxie` : "Voxie";
  }, [unread]);

  const section: Section = route.screen === "chats" || route.screen === "board" || route.screen === "document" ? "chats" : route.screen;
  const open = (conversation: string, message?: string) => navigate({ screen: "chats", conversation, message });

  let main;
  if (route.screen === "settings") main = <Settings />;
  else if (route.screen === "document") main = <DocumentView id={route.id} onBack={(c) => navigate(c ? { screen: "chats", conversation: c } : { screen: "chats" })} />;
  else if (route.screen === "board") main = <BoardView id={route.id} onBack={(c) => navigate(c ? { screen: "chats", conversation: c } : { screen: "chats" })} />;
  else if (route.screen === "games")
    main = <Placeholder title="Spiele" text="Die Spiele kommen in einem späteren Schritt in den Browser. Bis dahin spielst du sie in der App." />;
  else if (route.screen === "voxie") main = <Placeholder title="Voxie" text={total ? "Dein Chat mit Voxie ist noch nicht da." : "Wird geladen …"} />;
  else
    main = (
      <>
        <div className={route.conversation ? `${styles.listPane} ${styles.hideNarrow}` : styles.listPane}>
          <ChatList selected={route.conversation ?? null} onOpen={open} />
        </div>
        {route.conversation ? (
          <Conversation
            id={route.conversation}
            focusMessage={route.message}
            onBack={() => navigate({ screen: "chats" })}
            onTopic={(topic) => navigate({ screen: "chats", conversation: route.conversation, topic })}
            onBoards={() => navigate({ screen: "chats", conversation: route.conversation, panel: route.panel === "boards" ? undefined : "boards" })}
            onOpenBoard={(id) => navigate({ screen: "board", id })}
            onDocuments={() => navigate({ screen: "chats", conversation: route.conversation, panel: route.panel === "documents" ? undefined : "documents" })}
            onOpenDocument={(id) => navigate({ screen: "document", id })}
          />
        ) : (
          <div className={styles.nothing}>
            <span>Wähle links einen Chat.</span>
          </div>
        )}
        {route.conversation && route.panel === "boards" && !route.topic && (
          <BoardList conversationId={route.conversation} onOpen={(id) => navigate({ screen: "board", id })} onClose={() => navigate({ screen: "chats", conversation: route.conversation })} />
        )}
        {route.conversation && route.panel === "documents" && !route.topic && (
          <DocumentList conversationId={route.conversation} onOpen={(id) => navigate({ screen: "document", id })} onClose={() => navigate({ screen: "chats", conversation: route.conversation })} />
        )}
        {route.conversation && route.topic && (
          <TopicPanel topicId={route.topic} onClose={() => navigate({ screen: "chats", conversation: route.conversation })} onOpenMessage={open} />
        )}
      </>
    );

  return (
    <div className={styles.shell}>
      <Rail
        active={section}
        unread={unread}
        onSelect={(s) => navigate(s === "chats" ? { screen: "chats" } : s === "settings" ? { screen: "settings" } : s === "voxie" ? { screen: "voxie" } : { screen: "games" })}
      />
      <div className={styles.column}>
        {inCall && minimized && <CallBar onOpen={() => setMinimized(false)} />}
        <div className={styles.content}>
          {inCall && !minimized ? <CallView onMinimize={() => setMinimized(true)} onOpenChat={(c) => navigate({ screen: "chats", conversation: c })} /> : main}
        </div>
      </div>
      <IncomingCall />
    </div>
  );
}

function Placeholder({ title, text }: { title: string; text: string }) {
  return (
    <main className={styles.placeholder}>
      <h1>{title}</h1>
      <p>{text}</p>
    </main>
  );
}
